import { compose } from "redux";
import { fromJS } from "immutable";
import { connect } from "react-redux";
import React, { useEffect } from "react";
import { Field } from "redux-form/immutable";
import { Grid, MenuItem } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import {
  SHIFT_TYPES,
  CREATE_CARD_MOVE_FORM,
} from "features/planner/planner.constants";

import {
  getCrewsFromState,
  getGroupFromState,
  getGroupProjectsFromState,
  getQualificationsFromState,
  getReplaceableEmployeesFromState,
} from "features/planner/planner.selectors";

import {
  GroupMapper,
  shiftTypeToString,
} from "features/planner/planner.mappers";

import { EMPTY_LIST } from "app/app.constants";
import { required } from "utils/validation.util";
import { invokeIfFunction } from "utils/app.util";
import TextField from "app/components/form/TextField";
import DatePickerField from "app/components/form/DatePickerField";
import { getSummarizedDataStatesFromState } from "app/app.selectors";
import { ACTIONS } from "features/planner/employee/employee.constants";
import { getEmployeesProjects } from "features/planner/employee/employee.actions";
import HoursDropDown from "features/planner/components/CreateCardMove/HoursDropDown";
import MinutesDropDown from "features/planner/components/CreateCardMove/MinutesDropDown";
import { getEmployeesProjectsCardIdFromState } from "features/planner/employee/employee.selectors";
import EmployeeProjectsContainer from "features/planner/components/Project/EmployeeProjectsContainer";
import DWIProjectsMultiSelectFieldList from "features/planner/components/Project/DWIProjectsMultiSelectFieldList";

const DeployOffshoreForm = ({
  crews,
  group,
  cardId,
  classes,
  projects,
  dataState,
  handleSubmit,
  dispatchOnLoad,
  qualifications,
  employeeProjects = EMPTY_LIST,
  existingEmployeesProjects = EMPTY_LIST,
  onAssignProject,
  onUnassignProject,
  replaceableEmployees,
  displayFromDate = false,
  isEmployeePage = false,
}) => {
  useEffect(() => {
    dispatchOnLoad(cardId);
  }, [dispatchOnLoad, cardId]);

  return (
    <form onSubmit={handleSubmit}>
      <Grid container>
        <Grid container item spacing={2}>
          <Grid container item xs={6}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Field
                  validate={[required]}
                  component={DatePickerField}
                  name={CREATE_CARD_MOVE_FORM.ARRIVAL_DATE}
                  DatePickerProps={{
                    disabled: !!displayFromDate,
                    margin: "normal",
                    label: `Arrival at ${group.get("name")}*`,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <HoursDropDown
                  disabled={!!displayFromDate}
                  name={CREATE_CARD_MOVE_FORM.ARRIVAL_HOUR}
                />
              </Grid>
              <Grid item xs={3}>
                <MinutesDropDown
                  disabled={!!displayFromDate}
                  name={CREATE_CARD_MOVE_FORM.ARRIVAL_MINUTE}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Field
              select
              label="Qualification"
              name={CREATE_CARD_MOVE_FORM.QUALIFICATION}
              component={TextField}
              SelectProps={{
                classes: {
                  select: classes.select,
                },
              }}
            >
              {qualifications.map(qualification => (
                <MenuItem
                  key={qualification.get("id")}
                  value={qualification.get("id")}
                >
                  <Grid container alignItems="center">
                    <Grid
                      item
                      className={classes.qualificationIcon}
                      style={{ background: qualification.get("backColor") }}
                    ></Grid>
                    <Grid item>{qualification.get("name")}</Grid>
                  </Grid>
                </MenuItem>
              ))}
            </Field>
          </Grid>
        </Grid>
        <Grid container item spacing={2}>
          <Grid container item xs={6}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Field
                  validate={[required]}
                  component={DatePickerField}
                  name={CREATE_CARD_MOVE_FORM.DEPARTURE_DATE}
                  DatePickerProps={{
                    margin: "normal",
                    label: `Departure from ${group.get("name")}*`,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <HoursDropDown name={CREATE_CARD_MOVE_FORM.DEPARTURE_HOUR} />
              </Grid>
              <Grid item xs={3}>
                <MinutesDropDown
                  name={CREATE_CARD_MOVE_FORM.DEPARTURE_MINUTE}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={6}>
            <Field
              select
              label="Crew"
              name={CREATE_CARD_MOVE_FORM.CREW}
              component={TextField}
            >
              {crews.map(crew => (
                <MenuItem key={crew.get("id")} value={crew.get("id")}>
                  {crew.get("name")}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={6}>
            {!!replaceableEmployees.size && (
              <Field
                select
                label="Replace"
                name={CREATE_CARD_MOVE_FORM.REPLACE_EMPLOYEE}
                component={TextField}
              >
                <MenuItem value={null} />
                {replaceableEmployees.map(employee => (
                  <MenuItem key={employee.get("id")} value={employee.get("id")}>
                    {employee.get("displayName")}
                  </MenuItem>
                ))}
              </Field>
            )}
          </Grid>
          <Grid item xs={6}>
            <Field
              select
              label="Shift"
              name={CREATE_CARD_MOVE_FORM.SHIFT_TYPE}
              component={TextField}
            >
              {Object.values(SHIFT_TYPES).map(shiftType => (
                <MenuItem key={shiftType} value={shiftType}>
                  {shiftTypeToString(shiftType)}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={12}>
            {!isEmployeePage && !dataState.isLoading() && (
              <Field
                format={null}
                label="Projects"
                projects={fromJS(projects)}
                employeeProjects={fromJS(existingEmployeesProjects)}
                component={DWIProjectsMultiSelectFieldList}
                name={CREATE_CARD_MOVE_FORM.GROUP_PROJECTS}
              />
            )}
            {isEmployeePage && (
              <EmployeeProjectsContainer
                onUnassignProject={onUnassignProject}
                onAssignProject={onAssignProject}
                projects={fromJS(projects)}
                employeeProjects={fromJS(employeeProjects)}
              />
            )}
          </Grid>
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={12}>
            <Field
              minRows={4}
              multiline
              label="Comment"
              margin="dense"
              component={TextField}
              name={CREATE_CARD_MOVE_FORM.DEPLOYMENT_COMMENT}
            />
          </Grid>
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={6} />
          <Grid item xs={6}>
            {displayFromDate && (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Field
                    component={DatePickerField}
                    name={CREATE_CARD_MOVE_FORM.FROM_DATE}
                    DatePickerProps={{
                      margin: "normal",
                      label: "From",
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <HoursDropDown name={CREATE_CARD_MOVE_FORM.FROM_HOUR} />
                </Grid>
                <Grid item xs={3}>
                  <MinutesDropDown name={CREATE_CARD_MOVE_FORM.FROM_MINUTE} />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={6} />
          <Grid item xs={6}></Grid>
        </Grid>
      </Grid>
    </form>
  );
};

DeployOffshoreForm.defaultProps = {
  group: GroupMapper.initial(),
  replaceableEmployees: EMPTY_LIST,
};

const styles = theme => ({
  select: {
    display: "flex",
    alignItems: "center",
  },
  qualificationIcon: {
    width: "1rem",
    height: "1rem",
    borderRadius: "0.5rem",
    marginRight: theme.spacing(0.5),
  },
});

export default compose(
  connect(
    (state, { formValueSelector, ...props }) => {
      const groupId =
        props.groupId ||
        invokeIfFunction(
          formValueSelector,
          state,
          CREATE_CARD_MOVE_FORM.TARGET_GROUP_ID,
        );

      const cardId = invokeIfFunction(
        formValueSelector,
        state,
        CREATE_CARD_MOVE_FORM.CARD,
      );

      return {
        dataState: getSummarizedDataStatesFromState(
          state,
          ACTIONS.GET_EMPLOYEES_PROJECTS,
        ),
        cardId: cardId,
        existingEmployeesProjects: getEmployeesProjectsCardIdFromState(
          state,
          cardId,
        )?.filter(ep => ep.get("groupId").toString() === groupId.toString()),
        crews: getCrewsFromState(state),
        qualifications: getQualificationsFromState(state),
        group: groupId ? getGroupFromState(state, groupId) : undefined,
        replaceableEmployees: getReplaceableEmployeesFromState(state, groupId),
        projects: groupId
          ? getGroupProjectsFromState(state, groupId)
          : EMPTY_LIST,
      };
    },
    {
      dispatchOnLoad: getEmployeesProjects,
    },
  ),
  React.memo,
  withStyles(styles),
)(DeployOffshoreForm);
